import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EbayListingSyncAutomationPanel from "./components/EbayListingSyncAutomationPanel";
import DailyDecisionAgentPanel from "./components/DailyDecisionAgentPanel";
import AdStopLossPanel from "./components/AdStopLossPanel";
import BusinessFeedbackPanel from "./components/businessfeedback";

const API_BASE = "/api/ebay-intelligence";
const EMPTY_SCHEMA_TABLES = {};
const EMPTY_ARRAY = [];
const LOCAL_CLEANUP_STATUSES = ["Out of Stock", "Sold", "Ended", "Needs Review"];
const LISTING_HEALTH_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "ISSUES_ONLY", label: "Issues only" },
  { value: "HEALTHY", label: "Healthy / not flagged" },
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "OUT_OF_STOCK", label: "OUT_OF_STOCK" },
  { value: "ENDED", label: "ENDED" },
  { value: "SYNC_FAILED", label: "Sync failed" },
  { value: "LISTED_QTY_GT_0", label: "Listed with quantity > 0" },
  { value: "LISTED_QTY_EQ_0", label: "Listed with quantity = 0" },
];
const ACTION_QUEUE_CATEGORY_FILTERS = [
  { value: "ALL", label: "All categories" },
  { value: "SOLD_OUT_CLEANUP", label: "Sold-out cleanup" },
  { value: "SYNC_FAILED", label: "Sync failed" },
  { value: "STALE_SYNC", label: "Stale sync" },
  { value: "PRICE_MISMATCH", label: "Price mismatch" },
  { value: "QUANTITY_MISMATCH", label: "Quantity mismatch" },
  { value: "HIGH_IMPRESSIONS_LOW_CTR", label: "High impressions / low CTR" },
  { value: "HIGH_VIEWS_NO_SALE", label: "High views / no sale" },
  { value: "AD_SPEND_NO_SALE", label: "Ad spend / no sale" },
  { value: "LOW_TRAFFIC", label: "Low traffic" },
  { value: "HEALTHY_NO_ACTION", label: "Healthy / no action" },
];
const ACTION_QUEUE_SEVERITY_FILTERS = [
  { value: "ALL", label: "All severities" },
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
  { value: "INFO", label: "Info" },
];
const ACTION_QUEUE_PRIORITY_FILTERS = [
  { value: "ALL", label: "All priorities" },
  { value: "HIGH", label: "High priority" },
  { value: "MEDIUM", label: "Medium priority" },
  { value: "LOW", label: "Low priority" },
  { value: "INFO", label: "Info" },
];
const ACTION_QUEUE_SYNC_FILTERS = [
  { value: "ALL", label: "All sync states" },
  { value: "synced", label: "Synced" },
  { value: "failed", label: "Failed" },
  { value: "pending", label: "Pending" },
  { value: "unknown", label: "Unknown" },
];
const ACTION_OUTCOME_STATUSES = [
  { value: "open", label: "Open" },
  { value: "reviewed", label: "Reviewed" },
  { value: "actioned", label: "Actioned" },
  { value: "deferred", label: "Deferred" },
  { value: "ignored", label: "Ignored" },
];
const ROUTINE_STEPS = [
  {
    key: "preflight",
    label: "Schema / preflight",
    helper: "Schema, config, mapping, and batch readiness",
  },
  {
    key: "local_baseline_snapshot",
    label: "Local baseline",
    helper: "Capture current app listing state",
    actionPath: "/snapshots/local-baseline",
    actionBody: { limit: 1000 },
    successPrefix: "Local baseline snapshots created",
  },
  {
    key: "ebay_offer_snapshot_refresh",
    label: "eBay refresh",
    helper: "Refresh linked eBay offer snapshots",
    actionPath: "/snapshots/refresh-ebay",
    actionBody: { limit: 25 },
    successPrefix: "Refreshed 25 eBay offer snapshots",
  },
  {
    key: "listings_sync_from_ebay",
    label: "Listing sync",
    helper: "Pull stale listed eBay fields into the app",
    actionPath: "/listings/sync-from-ebay",
    actionBody: { limit: 25, filter_mode: "stale_listed" },
    successPrefix: "Synced 25 stale listings from eBay",
  },
  {
    key: "listing_traffic_import",
    label: "Traffic import",
    helper: "Import 30-day Analytics listing telemetry",
    actionPath: "/imports/listing-traffic",
    actionBody: { days: 30, limit: 1000 },
    successPrefix: "Imported listing traffic",
  },
  {
    key: "promoted_listing_performance_import",
    label: "Ad import",
    helper: "Create/import Promoted Listings CPS report data",
    actionPath: "/imports/promoted-listings",
    actionBody: { days: 30 },
    successPrefix: "Imported Promoted Listings performance",
  },
  {
    key: "priority_cpc_performance_import",
    label: "Priority CPC",
    helper: "Create/import Priority CPC report data",
    actionPath: "/imports/priority-cpc",
    actionBody: { days: 30 },
    successPrefix: "Imported Priority CPC performance",
  },
  {
    key: "action_queue_review",
    label: "Action Queue review",
    helper: "Review cleanup, sync, price, traffic, and ad signals",
  },
];

const money = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value);
};

const numberText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : String(value);
};

const dateText = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return String(value);
  }
};

const percentText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : String(value);
};

const formatImportResult = (json) => {
  const parts = [
    `imported ${numberText(json.rows_imported || 0)}`,
    `updated ${numberText(json.rows_updated || 0)}`,
    `matched ${numberText(json.listings_matched || 0)}`,
    `unmatched ${numberText(json.listings_unmatched || 0)}`,
  ];
  if (json.campaigns_imported !== undefined) parts.push(`campaigns ${numberText(json.campaigns_imported || 0)}`);
  if (json.report_task_created !== undefined) parts.push(`report task ${json.report_task_created ? "created" : "not created"}`);
  if (json.report_tasks_processed !== undefined) parts.push(`tasks processed ${numberText(json.report_tasks_processed || 0)}`);
  if (json.errors?.length) parts.push(`errors ${numberText(json.errors.length)}`);
  if (json.latest_import_timestamp) parts.push(`latest ${dateText(json.latest_import_timestamp)}`);
  return parts.join(" / ");
};

const runCountSummary = (run) => {
  if (!run) return "No run recorded";
  const parts = [
    `imported ${numberText(run.rows_imported || 0)}`,
    `updated ${numberText(run.rows_updated || 0)}`,
    `matched ${numberText(run.rows_matched || 0)}`,
    `unmatched ${numberText(run.rows_unmatched || 0)}`,
  ];
  return parts.join(" / ");
};

const failureText = (run) => {
  if (!run) return "";
  const status = run.status ? `${run.status}: ` : "";
  return `${status}${run.error_message || "No error message captured."}`;
};

const runStatusChip = (step, preflightReady) => {
  if (step.key === "preflight") {
    return preflightReady ? <Chip size="small" color="success" label="Ready" /> : <Chip size="small" color="warning" label="Needs attention" />;
  }
  if (step.lastFailure) return <Chip size="small" color="warning" label="Latest failure" />;
  if (step.lastSuccess) return <Chip size="small" color="success" label="Last success" />;
  return <Chip size="small" label="Not run" />;
};

const loadOptionalPreflight = async () => {
  try {
    const res = await fetch(`${API_BASE}/sync-import-preflight`);
    const json = await res.json();
    if (!res.ok || !json.ok) {
      return {
        ok: false,
        error: json.error || "Preflight endpoint unavailable",
        recommended_next_action: "Use the existing manual import controls, then refresh once backend preflight is available.",
      };
    }
    return json;
  } catch (e) {
    return {
      ok: false,
      error: e.message || String(e),
      recommended_next_action: "Use the existing manual import controls, then refresh once backend preflight is available.",
    };
  }
};

const loadOptionalAgentBriefing = async () => {
  try {
    const res = await fetch(`${API_BASE}/daily-summary/agent-briefing`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ limit: 1000, include_prompt: false }),
    });
    const json = await res.json();
    if (!res.ok) {
      return {
        ok: false,
        status: "unavailable",
        message: json.error || "Agent briefing endpoint unavailable",
      };
    }
    return json;
  } catch (e) {
    return {
      ok: false,
      status: "unavailable",
      message: e.message || String(e),
    };
  }
};

const issueLabel = (value) => {
  const flag = value || "OK_OR_NEEDS_DATA";
  if (flag === "SYNC_FAILED") return "Sync failed";
  if (flag === "APP_LISTED_EBAY_OUT_OF_STOCK") return "App listed / eBay out of stock";
  if (flag === "APP_LISTED_EBAY_ENDED") return "App listed / eBay ended";
  if (flag === "APP_LISTED_OFFER_UNPUBLISHED") return "App listed / offer unpublished";
  if (flag === "MISSING_EBAY_ID") return "Missing eBay ID";
  if (flag === "MISSING_EBAY_OFFER_ID") return "Missing offer ID";
  if (flag === "STALE_SYNC") return "Stale sync";
  if (flag === "PRICE_MISMATCH") return "Price mismatch";
  if (flag === "QUANTITY_MISMATCH") return "Qty mismatch";
  if (flag === "MISSING_EBAY_STATUS") return "Missing eBay status";
  if (flag === "HIGH_IMPRESSIONS_LOW_CTR") return "High impressions / low CTR";
  if (flag === "HIGH_VIEWS_NO_SALE") return "High views / no sale";
  if (flag === "AD_SPEND_NO_SALE") return "Ad spend / no sale";
  return "Needs data / OK";
};

const actionCategoryLabel = (value) => {
  const option = ACTION_QUEUE_CATEGORY_FILTERS.find((item) => item.value === value);
  return option ? option.label : (value || "Unknown");
};

const headlineStatusChip = (status) => {
  if (status === "attention_required") return <Chip size="small" color="error" label="Attention required" />;
  if (status === "review_recommended") return <Chip size="small" color="warning" label="Review recommended" />;
  if (status === "data_incomplete") return <Chip size="small" color="warning" label="Data incomplete" />;
  if (status === "steady") return <Chip size="small" color="success" label="Steady" />;
  return <Chip size="small" label={status || "Summary pending"} />;
};

const freshnessText = (label, hoursOld) => {
  if (hoursOld === null || hoursOld === undefined || hoursOld === "") return `${label}: missing`;
  const n = Number(hoursOld);
  if (!Number.isFinite(n)) return `${label}: ${hoursOld}`;
  return `${label}: ${n.toFixed(1)}h old`;
};

const healthChip = (value) => {
  const flag = value || "OK_OR_NEEDS_DATA";
  if (["SYNC_FAILED", "APP_LISTED_EBAY_OUT_OF_STOCK", "APP_LISTED_EBAY_ENDED", "PRICE_MISMATCH", "QUANTITY_MISMATCH", "AD_SPEND_NO_SALE"].includes(flag)) {
    return <Chip size="small" color="error" label={issueLabel(flag)} />;
  }
  if (["APP_LISTED_OFFER_UNPUBLISHED", "MISSING_EBAY_ID", "MISSING_EBAY_OFFER_ID", "STALE_SYNC", "MISSING_EBAY_STATUS"].includes(flag)) {
    return <Chip size="small" color="warning" label={issueLabel(flag)} />;
  }
  if (["HIGH_IMPRESSIONS_LOW_CTR", "HIGH_VIEWS_NO_SALE"].includes(flag)) {
    return <Chip size="small" color="info" label={issueLabel(flag)} />;
  }
  return <Chip size="small" label={issueLabel(flag)} />;
};

const actionCategoryChip = (value) => {
  const category = value || "HEALTHY_NO_ACTION";
  if (["SOLD_OUT_CLEANUP", "SYNC_FAILED"].includes(category)) {
    return <Chip size="small" color="error" label={actionCategoryLabel(category)} />;
  }
  if (["STALE_SYNC", "PRICE_MISMATCH", "QUANTITY_MISMATCH", "AD_SPEND_NO_SALE"].includes(category)) {
    return <Chip size="small" color="warning" label={actionCategoryLabel(category)} />;
  }
  if (["HIGH_IMPRESSIONS_LOW_CTR", "HIGH_VIEWS_NO_SALE", "LOW_TRAFFIC"].includes(category)) {
    return <Chip size="small" color="info" label={actionCategoryLabel(category)} />;
  }
  return <Chip size="small" label={actionCategoryLabel(category)} />;
};

const severityChip = (value) => {
  const severity = value || "INFO";
  if (severity === "HIGH") return <Chip size="small" color="error" label="High" />;
  if (severity === "MEDIUM") return <Chip size="small" color="warning" label="Medium" />;
  if (severity === "LOW") return <Chip size="small" color="info" label="Low" />;
  return <Chip size="small" label="Info" />;
};

const repricingChip = (candidate, priority) => {
  if (!candidate) return <Chip size="small" label="No price review" />;
  if (priority === "HIGH") return <Chip size="small" color="error" label="Review price" />;
  if (priority === "MEDIUM") return <Chip size="small" color="warning" label="Review price" />;
  if (priority === "NEEDS_DATA") return <Chip size="small" color="info" label="Needs data" />;
  return <Chip size="small" color="info" label="Review listing" />;
};

const adDiagnosticChip = (value) => {
  const category = value || "NO_AD_DATA";
  if (category === "AD_SPEND_NO_SALE" || category === "LOW_ROAS") {
    return <Chip size="small" color="error" label={category.replace(/_/g, " ")} />;
  }
  if (category === "MISSING_ATTRIBUTION_DATA") {
    return <Chip size="small" color="warning" label="MISSING ATTRIBUTION" />;
  }
  if (category === "GOOD_ROAS") {
    return <Chip size="small" color="success" label="GOOD ROAS" />;
  }
  return <Chip size="small" label="NO AD DATA" />;
};

function MetricCard({ label, value, helper }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
        {value ?? "--"}
      </Typography>
      {helper && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {helper}
        </Typography>
      )}
    </Paper>
  );
}

export default function EbayIntelligenceDashboard() {
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [prodChecks, setProdChecks] = useState(null);
  const [rows, setRows] = useState([]);
  const [listingHealthSummary, setListingHealthSummary] = useState({});
  const [listingHealthTotalRows, setListingHealthTotalRows] = useState(0);
  const [listingHealthFilter, setListingHealthFilter] = useState("ALL");
  const [listingHealthSearch, setListingHealthSearch] = useState("");
  const [listingHealthPagination, setListingHealthPagination] = useState({ page: 0, pageSize: 50 });
  const [actionQueueRows, setActionQueueRows] = useState([]);
  const [actionQueueSummary, setActionQueueSummary] = useState({});
  const [actionQueueAdSummary, setActionQueueAdSummary] = useState({});
  const [actionQueuePrioritySummary, setActionQueuePrioritySummary] = useState({});
  const [actionQueueOutcomeSummary, setActionQueueOutcomeSummary] = useState({});
  const [actionQueueSyncSummary, setActionQueueSyncSummary] = useState({});
  const [dailySummary, setDailySummary] = useState(null);
  const [dailyAgentBriefing, setDailyAgentBriefing] = useState(null);
  const [agentBriefingLoading, setAgentBriefingLoading] = useState(false);
  const [syncPreflight, setSyncPreflight] = useState(null);
  const [actionQueueCategoryFilter, setActionQueueCategoryFilter] = useState("ALL");
  const [actionQueueSeverityFilter, setActionQueueSeverityFilter] = useState("ALL");
  const [actionQueuePriorityFilter, setActionQueuePriorityFilter] = useState("ALL");
  const [actionQueueOutcomeFilter, setActionQueueOutcomeFilter] = useState("ALL");
  const [actionQueueSyncFilter, setActionQueueSyncFilter] = useState("ALL");
  const [actionQueueSearch, setActionQueueSearch] = useState("");
  const [snapshotRows, setSnapshotRows] = useState([]);
  const [syncHealth, setSyncHealth] = useState([]);
  const [syncIssues, setSyncIssues] = useState([]);
  const [issueFilter, setIssueFilter] = useState("ALL");
  const [selectedActionIds, setSelectedActionIds] = useState([]);
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupStatus, setCleanupStatus] = useState("Out of Stock");
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [outcomeDraft, setOutcomeDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listingParams = new URLSearchParams({
        limit: String(listingHealthPagination.pageSize),
        offset: String(listingHealthPagination.page * listingHealthPagination.pageSize),
        filter: listingHealthFilter,
        search: listingHealthSearch,
      });
      const [overviewRes, checksRes, listingsRes, actionQueueRes, dailySummaryRes, snapshotsRes, healthRes, issuesRes, preflightJson] = await Promise.all([
        fetch(`${API_BASE}/overview`),
        fetch(`${API_BASE}/prod-data-checks`),
        fetch(`${API_BASE}/listings?${listingParams.toString()}`),
        fetch(`${API_BASE}/action-queue?limit=1000`),
        fetch(`${API_BASE}/daily-summary?limit=1000`),
        fetch(`${API_BASE}/snapshots?limit=500`),
        fetch(`${API_BASE}/sync-health`),
        fetch(`${API_BASE}/sync-issues?limit=500`),
        loadOptionalPreflight(),
      ]);

      const overviewJson = await overviewRes.json();
      const checksJson = await checksRes.json();
      const listingsJson = await listingsRes.json();
      const actionQueueJson = await actionQueueRes.json();
      const dailySummaryJson = await dailySummaryRes.json();
      const snapshotsJson = await snapshotsRes.json();
      const healthJson = await healthRes.json();
      const issuesJson = await issuesRes.json();

      if (!overviewRes.ok || !overviewJson.ok) throw new Error(overviewJson.error || "Failed to load overview");
      if (!checksRes.ok || !checksJson.ok) throw new Error(checksJson.error || "Failed to load prod data checks");
      if (!listingsRes.ok || !listingsJson.ok) throw new Error(listingsJson.error || "Failed to load listings");
      if (!actionQueueRes.ok || !actionQueueJson.ok) throw new Error(actionQueueJson.error || "Failed to load action queue");
      if (!dailySummaryRes.ok || !dailySummaryJson.ok) throw new Error(dailySummaryJson.error || "Failed to load daily summary");
      if (!snapshotsRes.ok || !snapshotsJson.ok) throw new Error(snapshotsJson.error || "Failed to load snapshot details");
      if (!healthRes.ok || !healthJson.ok) throw new Error(healthJson.error || "Failed to load sync health");
      if (!issuesRes.ok || !issuesJson.ok) throw new Error(issuesJson.error || "Failed to load sync issues");

      setOverview(overviewJson);
      setProdChecks(checksJson);
      setRows(listingsJson.rows || []);
      setListingHealthSummary(listingsJson.summary || {});
      setListingHealthTotalRows(listingsJson.pagination?.total_rows || 0);
      setActionQueueRows(actionQueueJson.rows || []);
      setActionQueueSummary(actionQueueJson.summary || {});
      setActionQueueAdSummary(actionQueueJson.ad_diagnostic_summary || {});
      setActionQueuePrioritySummary(actionQueueJson.priority_summary || {});
      setActionQueueOutcomeSummary(actionQueueJson.outcome_status_summary || {});
      setActionQueueSyncSummary(actionQueueJson.sync_status_summary || {});
      setDailySummary(dailySummaryJson);
      setSnapshotRows(snapshotsJson.rows || []);
      setSyncHealth(healthJson.buckets || []);
      setSyncIssues(issuesJson.rows || []);
      setSyncPreflight(preflightJson);
      setSelectedActionIds([]);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [listingHealthFilter, listingHealthPagination.page, listingHealthPagination.pageSize, listingHealthSearch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const runAgentBriefing = async () => {
    setAgentBriefingLoading(true);
    setError(null);
    setDailyAgentBriefing(null);
    try {
      const briefingJson = await loadOptionalAgentBriefing();
      setDailyAgentBriefing(briefingJson);
      if (briefingJson?.ok) {
        setMessage("Agent briefing generated.");
      }
    } catch (e) {
      setDailyAgentBriefing({
        ok: false,
        status: "unavailable",
        message: e.message || String(e),
      });
    } finally {
      setAgentBriefingLoading(false);
    }
  };

  const handleListingHealthFilterChange = (event) => {
    setListingHealthFilter(event.target.value);
    setListingHealthPagination((current) => ({ ...current, page: 0 }));
  };

  const handleListingHealthSearchChange = (event) => {
    setListingHealthSearch(event.target.value);
    setListingHealthPagination((current) => ({ ...current, page: 0 }));
  };

  const runAction = async (path, body, successPrefix) => {
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || "Action failed");
      setMessage(`${successPrefix}: ${formatImportResult(json)}`);
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActionLoading(false);
    }
  };

  const listingSummary = overview?.listing_summary || {};
  const snapshotSummary = overview?.snapshot_summary || {};
  const trafficSummary = overview?.traffic_summary || {};
  const adSummary = overview?.ad_summary || {};
  const adReportTaskSummary = overview?.ad_report_task_summary || {};
  const dailySummaryCounts = dailySummary?.summary || {};
  const dailyOutcomeCounts = dailySummary?.outcome_status_counts || {};
  const dailyWarnings = dailySummary?.warnings || [];
  const dailyTopThemes = dailySummary?.top_cleanup_themes || [];
  const dailyTopRisks = dailySummary?.high_priority_open?.length ? dailySummary.high_priority_open : (dailySummary?.top_risks || []);
  const dailyTelemetryFreshness = dailySummary?.telemetry_freshness || {};
  const dailyImportFreshness = dailySummary?.import_freshness || {};
  const agentSummary = dailyAgentBriefing?.agent_summary || null;
  const agentStatus = dailyAgentBriefing?.status || "not_requested";
  const schemaTables = overview?.schema?.tables || EMPTY_SCHEMA_TABLES;
  const readyChecks = prodChecks?.ready_checks || EMPTY_ARRAY;
  const listingChecks = prodChecks?.listing_checks || {};
  const snapshotBySource = prodChecks?.snapshot_by_source || EMPTY_ARRAY;
  const recentErrors = prodChecks?.recent_errors || EMPTY_ARRAY;
  const preflightRunTypes = syncPreflight?.run_history?.run_types || {};
  const preflightReady = Boolean(syncPreflight?.schema_ready && syncPreflight?.config_ready && syncPreflight?.mapping_ready);
  const routineSteps = useMemo(() => {
    return ROUTINE_STEPS.map((step) => {
      const runInfo = preflightRunTypes[step.key] || {};
      return {
        ...step,
        lastSuccess: runInfo.last_success || null,
        lastFailure: runInfo.latest_failure || null,
      };
    });
  }, [preflightRunTypes]);
  const latestRoutineFailure = useMemo(() => {
    return routineSteps
      .map((step) => ({ ...step, failure: step.lastFailure }))
      .filter((step) => step.failure)
      .sort((a, b) => {
        const left = new Date(a.failure.completed_at || a.failure.started_at || 0).getTime();
        const right = new Date(b.failure.completed_at || b.failure.started_at || 0).getTime();
        return right - left;
      })[0] || null;
  }, [routineSteps]);

  const actionIssueSummary = useMemo(() => {
    const counts = {};
    syncIssues.forEach((row) => {
      const issue = row.issue_type || "UNKNOWN";
      counts[issue] = (counts[issue] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([issue, count]) => ({ issue, count }))
      .sort((a, b) => b.count - a.count);
  }, [syncIssues]);

  const actionRows = useMemo(() => {
    if (issueFilter === "ALL") return syncIssues;
    return syncIssues.filter((row) => row.issue_type === issueFilter);
  }, [syncIssues, issueFilter]);

  const actionQueueSummaryRows = useMemo(() => {
    const priority = ACTION_QUEUE_CATEGORY_FILTERS.reduce((acc, item, index) => ({ ...acc, [item.value]: index }), {});
    return Object.entries(actionQueueSummary)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => (priority[a.category] ?? 999) - (priority[b.category] ?? 999));
  }, [actionQueueSummary]);

  const actionQueueFilteredRows = useMemo(() => {
    const searchText = actionQueueSearch.trim().toLowerCase();
    return actionQueueRows.filter((row) => {
      if (actionQueueCategoryFilter !== "ALL" && row.category !== actionQueueCategoryFilter) return false;
      if (actionQueueSeverityFilter !== "ALL" && row.severity !== actionQueueSeverityFilter) return false;
      if (actionQueuePriorityFilter !== "ALL" && (row.priority_band || row.severity) !== actionQueuePriorityFilter) return false;
      if (actionQueueOutcomeFilter !== "ALL" && (row.outcome_status || "open") !== actionQueueOutcomeFilter) return false;
      if (actionQueueSyncFilter !== "ALL" && (row.ebay_sync_status || "unknown") !== actionQueueSyncFilter) return false;
      if (!searchText) return true;
      return [row.sku, row.card_name, row.title, row.reason, row.recommended_action, row.operator_note].some((value) => String(value || "").toLowerCase().includes(searchText));
    });
  }, [actionQueueCategoryFilter, actionQueueOutcomeFilter, actionQueuePriorityFilter, actionQueueRows, actionQueueSearch, actionQueueSeverityFilter, actionQueueSyncFilter]);

  const repricingCandidateCount = useMemo(() => {
    return actionQueueFilteredRows.filter((row) => row.repricing_candidate).length;
  }, [actionQueueFilteredRows]);

  const selectedCleanupRows = useMemo(() => {
    const selectedSet = new Set(selectedActionIds.map((id) => Number(id)));
    return actionRows.filter((row) => selectedSet.has(Number(row.id)));
  }, [actionRows, selectedActionIds]);

  const selectedCleanupEligibleCount = useMemo(() => {
    return selectedCleanupRows.filter((row) => row.issue_type === "APP_LISTED_EBAY_OUT_OF_STOCK").length;
  }, [selectedCleanupRows]);

  const handleActionSelectionChange = (selectionModel) => {
    if (Array.isArray(selectionModel)) {
      setSelectedActionIds(selectionModel);
      return;
    }
    setSelectedActionIds(Array.from(selectionModel?.ids ?? []));
  };

  const runSoldOutCleanup = async () => {
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/sync-issues/bulk-local-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listing_ids: selectedActionIds,
          new_status: cleanupStatus,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || "Cleanup failed");
      setCleanupDialogOpen(false);
      setMessage(`Local cleanup complete: updated ${json.updated || 0} listing(s) to ${json.new_status}. Skipped ${json.skipped || 0}.`);
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActionLoading(false);
    }
  };

  const saveActionOutcome = async (row, status, operatorNote) => {
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/action-outcomes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listing_id: row.listing_id,
          action_type: row.action_type || row.category,
          status,
          operator_note: operatorNote || "",
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || "Outcome save failed");
      setOutcomeDialogOpen(false);
      setOutcomeDraft(null);
      setMessage(`Action outcome saved for listing ${row.listing_id}.`);
      await loadData();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActionLoading(false);
    }
  };

  const openOutcomeDialog = (row) => {
    setOutcomeDraft({
      row,
      status: row.outcome_status || "open",
      operator_note: row.operator_note || "",
    });
    setOutcomeDialogOpen(true);
  };

  const jumpToActionQueue = ({ category = "ALL", severity = "ALL", priority = "ALL", outcome = "ALL", sync = "ALL" } = {}) => {
    setActionQueueCategoryFilter(category);
    setActionQueueSeverityFilter(severity);
    setActionQueuePriorityFilter(priority);
    setActionQueueOutcomeFilter(outcome);
    setActionQueueSyncFilter(sync);
    setTab(5);
  };

  const openRepricingFromActionQueue = (mode = "store_slowdown_boost") => {
    window.dispatchEvent(
      new CustomEvent("app:navigate-module", {
        detail: {
          module: "repricing",
          params: {
            repricing_tab: "campaign_modes",
            repricing_mode: mode,
            source: "ebay_intelligence_action_queue",
            action_category: actionQueueCategoryFilter,
            action_priority: actionQueuePriorityFilter,
          },
        },
      })
    );
  };

  const schemaIssues = useMemo(() => {
    return Object.entries(schemaTables)
      .filter(([, status]) => !status?.ready)
      .map(([name, status]) => ({
        name,
        exists: Boolean(status?.exists),
        missingColumns: status?.missing_columns || [],
      }));
  }, [schemaTables]);

  const listingColumns = [
    { field: "health_flag", headerName: "Health", width: 190, renderCell: (params) => healthChip(params.value) },
    { field: "sku", headerName: "SKU", width: 110 },
    { field: "card_name", headerName: "Card", width: 230 },
    { field: "listing_status", headerName: "App Status", width: 130 },
    { field: "ebay_sync_status", headerName: "Sync", width: 110 },
    { field: "ebay_offer_status", headerName: "Offer Status", width: 135 },
    { field: "ebay_listing_live_status", headerName: "eBay Listing Status", width: 155 },
    { field: "listing_price", headerName: "App Price", width: 105, renderCell: (params) => money(params.value) },
    { field: "ebay_current_price", headerName: "eBay Price", width: 105, renderCell: (params) => money(params.value) },
    { field: "ebay_available_quantity", headerName: "App Qty", width: 90, renderCell: (params) => numberText(params.value) },
    { field: "snapshot_available_quantity", headerName: "eBay Qty", width: 95, renderCell: (params) => numberText(params.value) },
    { field: "impressions_30d", headerName: "Impr. 30d", width: 105, renderCell: (params) => numberText(params.value) },
    { field: "views_30d", headerName: "Views 30d", width: 100, renderCell: (params) => numberText(params.value) },
    { field: "watchers_30d", headerName: "Watchers", width: 100, renderCell: (params) => numberText(params.value) },
    { field: "ctr_30d", headerName: "CTR 30d", width: 95, renderCell: (params) => percentText(params.value) },
    { field: "ad_fee_30d", headerName: "Ad Fee 30d", width: 115, renderCell: (params) => money(params.value) },
    { field: "cps_ad_fee_30d", headerName: "CPS Fee 30d", width: 115, renderCell: (params) => money(params.value) },
    { field: "cpc_spend_30d", headerName: "CPC Spend 30d", width: 130, renderCell: (params) => money(params.value) },
    { field: "total_ad_cost_30d", headerName: "Total Ad Cost 30d", width: 145, renderCell: (params) => money(params.value) },
    { field: "cpc_clicks_30d", headerName: "CPC Clicks 30d", width: 130, renderCell: (params) => numberText(params.value) },
    { field: "ad_sales_30d", headerName: "Ad Sales 30d", width: 120, renderCell: (params) => money(params.value) },
    { field: "cpc_roas_30d", headerName: "CPC ROAS", width: 105, renderCell: (params) => params.value === null || params.value === undefined ? "" : Number(params.value).toFixed(2) },
    { field: "roas_30d", headerName: "Overall ROAS", width: 115, renderCell: (params) => params.value === null || params.value === undefined ? "" : Number(params.value).toFixed(2) },
    { field: "last_ebay_sync_at", headerName: "Last Sync", width: 180, renderCell: (params) => dateText(params.value) },
  ];

  const snapshotColumns = [
    { field: "synced_at", headerName: "Synced At", width: 180, renderCell: (params) => dateText(params.value) },
    { field: "sku", headerName: "SKU", width: 110 },
    { field: "card_name", headerName: "Card", width: 220 },
    { field: "title", headerName: "Snapshot Title", flex: 1, minWidth: 260 },
    { field: "source", headerName: "Source", width: 145 },
    { field: "listing_status", headerName: "App Status", width: 120 },
    { field: "ebay_listing_status", headerName: "eBay Status", width: 130 },
    { field: "current_price", headerName: "eBay Price", width: 105, renderCell: (params) => money(params.value) },
    { field: "available_quantity", headerName: "eBay Qty", width: 95, renderCell: (params) => numberText(params.value) },
    { field: "quantity_sold", headerName: "Sold", width: 80, renderCell: (params) => numberText(params.value) },
    { field: "ebay_offer_id", headerName: "Offer ID", width: 150 },
    { field: "ebay_listing_id", headerName: "Listing ID", width: 150 },
  ];

  const syncColumns = [
    { field: "issue", headerName: "Issue", flex: 1, minWidth: 240 },
    { field: "count", headerName: "Count", width: 120, renderCell: (params) => numberText(params.value) },
  ];

  const issueColumns = [
    { field: "issue_type", headerName: "Issue", width: 245, renderCell: (params) => healthChip(params.value) },
    { field: "sku", headerName: "SKU", width: 110 },
    { field: "card_name", headerName: "Card", width: 220 },
    { field: "listing_status", headerName: "App Status", width: 120 },
    { field: "ebay_offer_status", headerName: "Offer Status", width: 135 },
    { field: "ebay_listing_live_status", headerName: "eBay Listing Status", width: 155 },
    { field: "listing_price", headerName: "App Price", width: 105, renderCell: (params) => money(params.value) },
    { field: "ebay_current_price", headerName: "eBay Price", width: 105, renderCell: (params) => money(params.value) },
    { field: "ebay_available_quantity", headerName: "App Qty", width: 90, renderCell: (params) => numberText(params.value) },
    { field: "snapshot_available_quantity", headerName: "eBay Qty", width: 95, renderCell: (params) => numberText(params.value) },
    { field: "last_ebay_sync_at", headerName: "Last Sync", width: 170, renderCell: (params) => dateText(params.value) },
    { field: "recommended_action", headerName: "Recommended Action", flex: 1, minWidth: 320 },
  ];

  const actionQueueColumns = [
    { field: "category", headerName: "Action", width: 210, renderCell: (params) => actionCategoryChip(params.value) },
    { field: "severity", headerName: "Severity", width: 110, renderCell: (params) => severityChip(params.value) },
    { field: "priority", headerName: "Priority", width: 90, renderCell: (params) => numberText(params.value) },
    { field: "priority_band", headerName: "Priority Band", width: 125, renderCell: (params) => severityChip(params.value) },
    {
      field: "outcome_status",
      headerName: "Outcome",
      width: 150,
      renderCell: (params) => (
        <Select
          size="small"
          value={params.row.outcome_status || "open"}
          disabled={actionLoading}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => saveActionOutcome(params.row, event.target.value, params.row.operator_note || "")}
          sx={{ minWidth: 132 }}
        >
          {ACTION_OUTCOME_STATUSES.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: "operator_note",
      headerName: "Note",
      width: 120,
      renderCell: (params) => (
        <Button size="small" variant={params.value ? "contained" : "outlined"} onClick={() => openOutcomeDialog(params.row)}>
          {params.value ? "Edit note" : "Add note"}
        </Button>
      ),
    },
    { field: "repricing_candidate", headerName: "Price Review", width: 135, renderCell: (params) => repricingChip(params.value, params.row.review_priority) },
    { field: "review_priority", headerName: "Review Priority", width: 135 },
    { field: "ad_diagnostic_category", headerName: "Ad Diagnostic", width: 170, renderCell: (params) => adDiagnosticChip(params.value) },
    { field: "sku", headerName: "SKU", width: 110 },
    { field: "card_name", headerName: "Card", width: 220 },
    { field: "title", headerName: "Title", flex: 1, minWidth: 260 },
    { field: "listing_status", headerName: "App Status", width: 120 },
    { field: "ebay_sync_status", headerName: "Sync", width: 105 },
    { field: "ebay_current_status", headerName: "eBay Status", width: 130 },
    { field: "current_price", headerName: "Current Price", width: 115, renderCell: (params) => money(params.value) },
    { field: "local_price", headerName: "App Price", width: 105, renderCell: (params) => money(params.value) },
    { field: "ebay_current_price", headerName: "eBay Price", width: 105, renderCell: (params) => money(params.value) },
    { field: "expected_profit", headerName: "Expected Profit", width: 130, renderCell: (params) => money(params.value) },
    { field: "expected_margin", headerName: "Margin", width: 95, renderCell: (params) => params.value === null || params.value === undefined ? "" : percentText(Number(params.value) * 100) },
    { field: "current_available_quantity", headerName: "Qty", width: 80, renderCell: (params) => numberText(params.value) },
    { field: "impressions_30d", headerName: "Impr. 30d", width: 105, renderCell: (params) => numberText(params.row.source_metrics?.impressions_30d) },
    { field: "views_30d", headerName: "Views 30d", width: 100, renderCell: (params) => numberText(params.row.source_metrics?.views_30d) },
    { field: "ctr_30d", headerName: "CTR 30d", width: 95, renderCell: (params) => percentText(params.row.source_metrics?.ctr_30d) },
    { field: "ad_cost_30d", headerName: "Ad Cost 30d", width: 120, renderCell: (params) => money(params.row.source_metrics?.total_ad_cost_30d) },
    { field: "ad_sales_30d", headerName: "Ad Sales 30d", width: 120, renderCell: (params) => money(params.row.source_metrics?.ad_sales_30d) },
    { field: "ad_roas_30d", headerName: "Ad ROAS", width: 105, renderCell: (params) => params.row.ad_diagnostics?.roas_30d === null || params.row.ad_diagnostics?.roas_30d === undefined ? "" : Number(params.row.ad_diagnostics.roas_30d).toFixed(2) },
    { field: "ad_diagnostic_reason", headerName: "Ad Reason", flex: 1, minWidth: 320 },
    { field: "last_ebay_sync_at", headerName: "Last Sync", width: 170, renderCell: (params) => dateText(params.value) },
    { field: "reason", headerName: "Why in Queue", flex: 1, minWidth: 360 },
    { field: "repricing_reason", headerName: "Review Reason", flex: 1, minWidth: 360 },
    { field: "recommended_action", headerName: "Recommended Action", flex: 1, minWidth: 360 },
  ];

  const recentErrorColumns = [
    { field: "sku", headerName: "SKU", width: 110 },
    { field: "card_name", headerName: "Card", width: 220 },
    { field: "ebay_offer_id", headerName: "Offer ID", width: 150 },
    { field: "ebay_sync_status", headerName: "Sync", width: 110 },
    { field: "last_ebay_sync_at", headerName: "Last Sync", width: 170, renderCell: (params) => dateText(params.value) },
    { field: "last_ebay_sync_error", headerName: "Error", flex: 1, minWidth: 300 },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            eBay Listing Intelligence
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Foundation telemetry layer for listing sync, listing traffic, promoted listing data, and future automation agents.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" color="secondary" onClick={() => openRepricingFromActionQueue("store_slowdown_boost")}>
            Open Repricing
          </Button>
          <Button variant="outlined" onClick={loadData} disabled={loading || actionLoading || agentBriefingLoading}>
            Refresh View
          </Button>
        </Stack>
      </Stack>

      {(loading || actionLoading || agentBriefingLoading) && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {schemaIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Manual database install is required before all telemetry features are available. Missing or incomplete objects: {schemaIssues.map((item) => item.name).join(", ")}.
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, next) => setTab(next)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Prod Checks" />
        <Tab label="Listing Health" />
        <Tab label="Snapshot Details" />
        <Tab label="Sync Health" />
        <Tab label="Action Queue" />
        <Tab label="Action Required" />
        <Tab label="Data Imports" />
        <Tab label="Daily Agent" />
        <Tab label="CPC Stop-Loss" />
        <Tab label="Business Feedback" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Daily Briefing
                    </Typography>
                    {headlineStatusChip(dailySummary?.headline_status)}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {dailySummary?.headline || "Loading deterministic summary and briefing context."}
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="contained" onClick={() => jumpToActionQueue({ priority: "HIGH" })}>
                    Work High Priority
                  </Button>
                  <Button variant="outlined" onClick={runAgentBriefing} disabled={agentBriefingLoading || loading || actionLoading}>
                    {agentBriefingLoading ? "Generating Briefing..." : "Generate Agent Briefing"}
                  </Button>
                  <Button variant="outlined" onClick={() => setTab(7)}>
                    Verify Imports
                  </Button>
                </Stack>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Import freshness
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                      {(dailyImportFreshness.stale_operations || []).slice(0, 4).map((item) => (
                        <Chip key={item} size="small" color="warning" label={`${item.replace(/_/g, " ")} stale`} sx={{ mb: 1 }} />
                      ))}
                      {(dailyImportFreshness.failed_operations || []).slice(0, 3).map((item) => (
                        <Chip key={item} size="small" color="error" label={`${item.replace(/_/g, " ")} failed`} sx={{ mb: 1 }} />
                      ))}
                      {!(dailyImportFreshness.stale_operations || []).length && !(dailyImportFreshness.failed_operations || []).length && (
                        <Chip size="small" color={dailyImportFreshness.schema_ready ? "success" : "warning"} label={dailyImportFreshness.schema_ready ? "Run history ready" : "Run history unavailable"} />
                      )}
                    </Stack>
                    {dailyImportFreshness.recommended_next_action && (
                      <Typography variant="body2" color="text.secondary">
                        {dailyImportFreshness.recommended_next_action}
                      </Typography>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Telemetry freshness
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <Chip size="small" label={freshnessText("Snapshot", dailyTelemetryFreshness.latest_snapshot_hours_old)} sx={{ mb: 1 }} />
                      <Chip size="small" label={freshnessText("Traffic", dailyTelemetryFreshness.latest_traffic_import_hours_old)} sx={{ mb: 1 }} />
                      <Chip size="small" label={freshnessText("Ads", dailyTelemetryFreshness.latest_ad_import_hours_old)} sx={{ mb: 1 }} />
                    </Stack>
                    {dailyWarnings.length > 0 ? (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        {dailyWarnings.slice(0, 2).join(" ")}
                      </Alert>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No freshness warnings in the deterministic summary.
                      </Typography>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Top categories
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                      {(dailyTopThemes.length ? dailyTopThemes : actionQueueSummaryRows).slice(0, 4).map((item) => (
                        <Chip
                          key={item.category}
                          size="small"
                          label={`${actionCategoryLabel(item.category)} (${numberText(item.count || 0)})`}
                          color="primary"
                          variant="outlined"
                          onClick={() => jumpToActionQueue({ category: item.category })}
                          sx={{ mb: 1 }}
                        />
                      ))}
                      {!dailyTopThemes.length && !actionQueueSummaryRows.length && <Chip size="small" label="No categories loaded" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {numberText(dailySummaryCounts.open_high_priority_actions || 0)} open high-priority action(s), {numberText(dailySummaryCounts.new_unreviewed_issues || 0)} new/unreviewed.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Operator briefing
                    </Typography>
                    {agentSummary ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {agentSummary.briefing_headline || dailySummary?.headline}
                        </Typography>
                        {(agentSummary.what_changed || []).slice(0, 3).map((item, index) => (
                          <Typography key={`changed-${index}`} variant="body2" color="text.secondary">
                            {item}
                          </Typography>
                        ))}
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", pt: 0.5 }}>
                          {(agentSummary.highest_leverage_cleanup_actions || []).slice(0, 3).map((item, index) => (
                            <Chip key={`cleanup-${index}`} size="small" label={item} sx={{ mb: 1, maxWidth: "100%" }} />
                          ))}
                        </Stack>
                      </>
                    ) : (
                      <Alert severity={agentStatus === "generation_failed" || agentStatus === "unavailable" ? "warning" : "info"} sx={{ py: 0.5 }}>
                        {dailyAgentBriefing?.message || "Agent briefing has not been generated yet. Use Generate Agent Briefing when you want an optional AI summary. The deterministic summary remains available."}
                      </Alert>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Top risks
                    </Typography>
                    {(agentSummary?.top_priorities?.length ? agentSummary.top_priorities : dailyTopRisks.slice(0, 3)).map((item, index) => {
                      const category = item.category || dailyTopRisks[index]?.category || "ALL";
                      const title = item.title || item.reason || item.card_name || dailyTopRisks[index]?.reason || actionCategoryLabel(category);
                      const why = item.why_it_matters || item.recommended_action || dailyTopRisks[index]?.recommended_action || "";
                      return (
                        <Box key={`${category}-${item.source_listing_id || item.listing_id || index}`} sx={{ borderLeft: "3px solid", borderColor: "primary.main", pl: 1.25 }}>
                          <Button size="small" variant="text" onClick={() => jumpToActionQueue({ category })} sx={{ px: 0, justifyContent: "flex-start", textAlign: "left" }}>
                            {numberText(item.rank || index + 1)}. {title}
                          </Button>
                          {why && (
                            <Typography variant="body2" color="text.secondary">
                              {why}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                    {!agentSummary?.top_priorities?.length && !dailyTopRisks.length && (
                      <Typography variant="body2" color="text.secondary">
                        No high-priority risks are currently present in the deterministic summary.
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Daily intelligence summary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Read-only rollup of Action Queue priorities and local workflow outcomes.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => jumpToActionQueue()}>
                Open Action Queue
              </Button>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="Open high priority" value={numberText(dailySummaryCounts.open_high_priority_actions)} helper="High severity and still open" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="New / unreviewed" value={numberText(dailySummaryCounts.new_unreviewed_issues)} helper={`${numberText(dailyOutcomeCounts.open || 0)} open outcome(s)`} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="Actioned today" value={numberText(dailySummaryCounts.actioned_today)} helper={`${numberText(dailySummaryCounts.reviewed_today || 0)} reviewed today`} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="Deferred / ignored" value={`${numberText(dailySummaryCounts.deferred || 0)} / ${numberText(dailySummaryCounts.ignored || 0)}`} helper="Local outcome tracking" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="Repricing candidates" value={numberText(dailySummaryCounts.repricing_candidates)} helper="Price/listing review" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="Ad spend risk" value={numberText(dailySummaryCounts.ad_spend_risk)} helper="Spend, ROAS, or attribution risk" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="Sync risk" value={numberText(dailySummaryCounts.sync_risk)} helper="Failed or stale sync" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard label="Sold-out cleanup" value={numberText(dailySummaryCounts.sold_out_cleanup_candidates)} helper="Local cleanup candidates" />
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Button size="small" variant="outlined" onClick={() => jumpToActionQueue({ severity: "HIGH" })}>
                    High Priority
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => jumpToActionQueue({ category: "SOLD_OUT_CLEANUP" })}>
                    Sold-Out Cleanup
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => jumpToActionQueue({ category: "SYNC_FAILED" })}>
                    Sync Failed
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => jumpToActionQueue({ category: "AD_SPEND_NO_SALE" })}>
                    Ad Spend Risk
                  </Button>
                  <Chip label={`Missing telemetry warnings: ${numberText(dailySummaryCounts.missing_telemetry_warnings || 0)}`} />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Total listings" value={numberText(listingSummary.total_listings)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Listed" value={numberText(listingSummary.listed_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Linked offer IDs" value={numberText(listingSummary.linked_offer_count)} helper="Can be refreshed from eBay offer API" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Missing offer IDs" value={numberText(listingSummary.missing_ebay_offer_id_count)} helper="Cannot be offer-refreshed yet" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Pre-listed" value={numberText(listingSummary.pre_listed_count)} helper="Not yet listed / not ended / not sold" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Photo uploaded" value={numberText(listingSummary.photo_uploaded_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Missing eBay ID" value={numberText(listingSummary.missing_ebay_listing_id_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Failed sync" value={numberText(listingSummary.failed_sync_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Stale listed sync" value={numberText(listingSummary.stale_sync_count)} helper="Listed but not synced in 24h" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Listing snapshots" value={numberText(snapshotSummary.snapshot_count)} helper={snapshotSummary.latest_snapshot_at ? `Latest ${dateText(snapshotSummary.latest_snapshot_at)}` : "No snapshots yet"} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="eBay API snapshots" value={numberText(snapshotSummary.ebay_offer_api_count)} helper="Source = ebay_offer_api" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Local baselines" value={numberText(snapshotSummary.local_baseline_count)} helper="Source = local_baseline" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Traffic rows" value={numberText(trafficSummary.traffic_rows)} helper={trafficSummary.latest_traffic_date ? `Latest ${trafficSummary.latest_traffic_date}` : "Awaiting import"} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Ad rows" value={numberText(adSummary.ad_rows)} helper={adSummary.latest_ad_date ? `Latest ${adSummary.latest_ad_date}` : "Awaiting import"} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Ad report tasks" value={numberText(adReportTaskSummary.ad_report_tasks)} helper={adReportTaskSummary.latest_ad_report_task_at ? `Latest ${dateText(adReportTaskSummary.latest_ad_report_task_at)}` : "Awaiting report task"} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Ad fee 30d" value={money(adSummary.ad_fee_30d)} helper="Legacy total fee field" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="CPS ad fee 30d" value={money(adSummary.cps_ad_fee_30d)} helper="Promoted Listings General" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="CPC spend 30d" value={money(adSummary.cpc_spend_30d)} helper="Priority / PPC" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Total ad cost 30d" value={money(adSummary.total_ad_cost_30d)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Ad ROAS 30d" value={adSummary.roas_30d ?? "--"} />
            </Grid>
          </Grid>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            This tab is the controlled prod validation checklist. It confirms schema readiness, eBay mapping coverage, snapshot history, and recent sync activity before traffic/ad automation is added.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {readyChecks.map((item) => (
              <Grid item xs={12} md={6} key={item.name}>
                <Alert severity={item.ready ? "success" : "warning"}>
                  <strong>{item.name}</strong><br />{item.detail}
                </Alert>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Listed with offer ID" value={numberText(listingChecks.listed_with_offer_id)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Listed missing offer ID" value={numberText(listingChecks.listed_missing_offer_id)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Synced listings" value={numberText(listingChecks.synced_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Synced last 24h" value={numberText(listingChecks.synced_last_24h)} />
            </Grid>
          </Grid>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Snapshot counts by source
            </Typography>
            <Stack spacing={1}>
              {snapshotBySource.length === 0 && <Typography color="text.secondary">No snapshot rows recorded yet.</Typography>}
              {snapshotBySource.map((item) => (
                <Alert key={item.source || "unknown"} severity="info">
                  <strong>{item.source || "unknown"}</strong>: {numberText(item.count)} rows. Latest: {dateText(item.latest_synced_at) || "none"}
                </Alert>
              ))}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Recent sync errors
            </Typography>
            <Box sx={{ height: 320 }}>
              <DataGrid
                rows={recentErrors}
                columns={recentErrorColumns}
                getRowId={(row) => row.id}
                pageSizeOptions={[10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                disableRowSelectionOnClick
              />
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Listing Health is read-only. Filters, search, and pagination only change which local rows are displayed; they do not update eBay or local listing records.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2}>
              <MetricCard label="Active eBay listings" value={numberText(listingHealthSummary.active_ebay_listings)} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MetricCard label="Out of stock" value={numberText(listingHealthSummary.out_of_stock_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MetricCard label="Ended" value={numberText(listingHealthSummary.ended_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MetricCard label="Sync failed" value={numberText(listingHealthSummary.sync_failed_count)} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MetricCard label="Healthy active listings" value={numberText(listingHealthSummary.healthy_active_count)} />
            </Grid>
          </Grid>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="listing-health-filter-label">Listing Health filter</InputLabel>
                <Select
                  labelId="listing-health-filter-label"
                  value={listingHealthFilter}
                  label="Listing Health filter"
                  onChange={handleListingHealthFilterChange}
                >
                  {LISTING_HEALTH_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search SKU or card name"
                value={listingHealthSearch}
                onChange={handleListingHealthSearchChange}
                sx={{ minWidth: 280 }}
              />
              <Typography variant="body2" color="text.secondary">
                Showing {numberText(rows.length)} of {numberText(listingHealthTotalRows)} matching listing(s).
              </Typography>
            </Stack>
          </Paper>
          <Box sx={{ height: 640 }}>
            <DataGrid
              rows={rows}
              columns={listingColumns}
              getRowId={(row) => row.id}
              pageSizeOptions={[25, 50, 100, 250]}
              paginationMode="server"
              rowCount={listingHealthTotalRows}
              paginationModel={listingHealthPagination}
              onPaginationModelChange={setListingHealthPagination}
              loading={loading}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      )}

      {tab === 3 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Recent snapshot rows captured from local baseline and eBay offer sync actions. This is the direct detail view for the data created by the Data Imports buttons.
          </Alert>
          <Box sx={{ height: 640 }}>
            <DataGrid
              rows={snapshotRows}
              columns={snapshotColumns}
              getRowId={(row) => row.id}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      )}

      {tab === 4 && (
        <Box sx={{ height: 420 }}>
          <DataGrid
            rows={syncHealth.map((row, idx) => ({ id: idx + 1, ...row }))}
            columns={syncColumns}
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            disableRowSelectionOnClick
          />
        </Box>
      )}

      {tab === 5 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Action Queue recommendations are advisory. Outcome status and notes are saved only to the local action outcome table; eBay and local listing lifecycle fields are not changed.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Queue rows" value={numberText(actionQueueFilteredRows.length)} helper={`${numberText(actionQueueRows.length)} loaded`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Open workflow" value={numberText(actionQueueOutcomeSummary.open || 0)} helper={`${numberText(actionQueueOutcomeSummary.reviewed || 0)} reviewed / ${numberText(actionQueueOutcomeSummary.actioned || 0)} actioned`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="High priority" value={numberText(actionQueuePrioritySummary.HIGH || 0)} helper={`${numberText(actionQueueSummary.SOLD_OUT_CLEANUP || 0)} sold-out cleanup`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Sync failed" value={numberText(actionQueueSyncSummary.failed || 0)} helper="Current local sync status" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Price/listing review" value={numberText(repricingCandidateCount)} helper="Advisory only" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Ad cost 30d" value={money(actionQueueAdSummary.total_ad_cost_30d)} helper={`CPS ${money(actionQueueAdSummary.cps_ad_fee_30d)} / CPC ${money(actionQueueAdSummary.cpc_spend_30d)}`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Ad sales / ROAS" value={money(actionQueueAdSummary.ad_sales_30d)} helper={actionQueueAdSummary.overall_roas_30d ? `ROAS ${Number(actionQueueAdSummary.overall_roas_30d).toFixed(2)}` : "ROAS unavailable"} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Ad risks" value={numberText((actionQueueAdSummary.ad_spend_no_sale_count || 0) + (actionQueueAdSummary.low_roas_count || 0))} helper={`${numberText(actionQueueAdSummary.no_ad_data_count || 0)} no ad data`} />
            </Grid>
            {actionQueueSummaryRows.slice(0, 3).map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.category}>
                <MetricCard label={actionCategoryLabel(item.category)} value={numberText(item.count)} />
              </Grid>
            ))}
          </Grid>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="action-queue-category-label">Action category</InputLabel>
                <Select
                  labelId="action-queue-category-label"
                  value={actionQueueCategoryFilter}
                  label="Action category"
                  onChange={(event) => setActionQueueCategoryFilter(event.target.value)}
                >
                  {ACTION_QUEUE_CATEGORY_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="action-queue-severity-label">Severity</InputLabel>
                <Select
                  labelId="action-queue-severity-label"
                  value={actionQueueSeverityFilter}
                  label="Severity"
                  onChange={(event) => setActionQueueSeverityFilter(event.target.value)}
                >
                  {ACTION_QUEUE_SEVERITY_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="action-queue-priority-label">Priority</InputLabel>
                <Select
                  labelId="action-queue-priority-label"
                  value={actionQueuePriorityFilter}
                  label="Priority"
                  onChange={(event) => setActionQueuePriorityFilter(event.target.value)}
                >
                  {ACTION_QUEUE_PRIORITY_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="action-queue-outcome-label">Outcome</InputLabel>
                <Select
                  labelId="action-queue-outcome-label"
                  value={actionQueueOutcomeFilter}
                  label="Outcome"
                  onChange={(event) => setActionQueueOutcomeFilter(event.target.value)}
                >
                  <MenuItem value="ALL">All outcomes</MenuItem>
                  {ACTION_OUTCOME_STATUSES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="action-queue-sync-label">Sync</InputLabel>
                <Select
                  labelId="action-queue-sync-label"
                  value={actionQueueSyncFilter}
                  label="Sync"
                  onChange={(event) => setActionQueueSyncFilter(event.target.value)}
                >
                  {ACTION_QUEUE_SYNC_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" color="secondary" onClick={() => openRepricingFromActionQueue("store_slowdown_boost")}>
                Reprice Candidates
              </Button>
              <Button variant="outlined" onClick={() => openRepricingFromActionQueue("dead_stock_cleanup")}>
                Dead Stock Run
              </Button>
              <TextField
                size="small"
                label="Search SKU, card, title, reason, note"
                value={actionQueueSearch}
                onChange={(event) => setActionQueueSearch(event.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Typography variant="body2" color="text.secondary">
                Showing {numberText(actionQueueFilteredRows.length)} action row(s).
              </Typography>
            </Stack>
          </Paper>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
            {actionQueueSummaryRows.map((item) => (
              <Chip
                key={item.category}
                label={`${actionCategoryLabel(item.category)} (${numberText(item.count)})`}
                color={actionQueueCategoryFilter === item.category ? "primary" : "default"}
                onClick={() => setActionQueueCategoryFilter(item.category)}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
          <Box sx={{ height: 640 }}>
            <DataGrid
              rows={actionQueueFilteredRows}
              columns={actionQueueColumns}
              getRowId={(row) => row.listing_id}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              loading={loading}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      )}

      {tab === 6 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Action Required classifies local listings against the latest eBay snapshot. Sold-out cleanup is local-only and does not update eBay, listing price, purchase order, profit, or source data.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Action rows" value={numberText(actionRows.length)} helper={issueFilter === "ALL" ? "All issue types" : issueLabel(issueFilter)} />
            </Grid>
            {actionIssueSummary.slice(0, 3).map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.issue}>
                <MetricCard label={issueLabel(item.issue)} value={numberText(item.count)} />
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Chip
              label={`All (${numberText(syncIssues.length)})`}
              color={issueFilter === "ALL" ? "primary" : "default"}
              onClick={() => setIssueFilter("ALL")}
              sx={{ mb: 1 }}
            />
            {actionIssueSummary.map((item) => (
              <Chip
                key={item.issue}
                label={`${issueLabel(item.issue)} (${numberText(item.count)})`}
                color={issueFilter === item.issue ? "primary" : "default"}
                onClick={() => setIssueFilter(item.issue)}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Sold-out local cleanup
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select rows classified as App listed / eBay out of stock, then mark the local app lifecycle status. eBay sync fields are preserved.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="warning"
                disabled={!selectedActionIds.length || selectedCleanupEligibleCount === 0 || actionLoading}
                onClick={() => setCleanupDialogOpen(true)}
              >
                Mark Selected Locally
              </Button>
            </Stack>
            {selectedActionIds.length > 0 && selectedCleanupEligibleCount === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Selected rows are not eligible for sold-out cleanup. Use rows with issue App listed / eBay out of stock.
              </Alert>
            ) : null}
          </Paper>
          <Box sx={{ height: 640 }}>
            <DataGrid
              rows={actionRows}
              columns={issueColumns}
              getRowId={(row) => row.id}
              checkboxSelection
              rowSelectionModel={{ type: "include", ids: new Set(selectedActionIds) }}
              onRowSelectionModelChange={handleActionSelectionChange}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      )}

      <Dialog open={cleanupDialogOpen} onClose={() => setCleanupDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Confirm local sold-out cleanup</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This will update local listing status only. It will not update eBay or alter price, purchase order, profit, source, or eBay sync data.
            </Alert>
            <Typography variant="body2">
              Selected rows: {numberText(selectedActionIds.length)}. Eligible App listed / eBay out of stock rows: {numberText(selectedCleanupEligibleCount)}.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="cleanup-status-label">New local status</InputLabel>
              <Select
                labelId="cleanup-status-label"
                value={cleanupStatus}
                label="New local status"
                onChange={(e) => setCleanupStatus(e.target.value)}
              >
                {LOCAL_CLEANUP_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={runSoldOutCleanup} disabled={actionLoading || selectedCleanupEligibleCount === 0}>
            Confirm local update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={outcomeDialogOpen} onClose={() => setOutcomeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Action outcome note</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              This saves local workflow tracking only. It does not update eBay or listing lifecycle fields.
            </Alert>
            <Typography variant="body2">
              Listing {outcomeDraft?.row?.listing_id}: {actionCategoryLabel(outcomeDraft?.row?.category)}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="outcome-status-label">Outcome status</InputLabel>
              <Select
                labelId="outcome-status-label"
                value={outcomeDraft?.status || "open"}
                label="Outcome status"
                onChange={(event) => setOutcomeDraft((current) => ({ ...current, status: event.target.value }))}
              >
                {ACTION_OUTCOME_STATUSES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Operator note"
              value={outcomeDraft?.operator_note || ""}
              onChange={(event) => setOutcomeDraft((current) => ({ ...current, operator_note: event.target.value }))}
              multiline
              minRows={4}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutcomeDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => saveActionOutcome(outcomeDraft.row, outcomeDraft.status, outcomeDraft.operator_note)}
            disabled={actionLoading || !outcomeDraft?.row}
          >
            Save Outcome
          </Button>
        </DialogActions>
      </Dialog>

      {tab === 7 && (
        <Box>
          <EbayListingSyncAutomationPanel
            onMessage={setMessage}
            onError={setError}
            onRefresh={loadData}
          />
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Sync/import routine
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Daily/weekly operator workflow for preflight, snapshots, eBay refresh, traffic, advertising telemetry, and Action Queue review.
          </Typography>
          {syncPreflight?.error && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Preflight status is not available yet: {syncPreflight.error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <MetricCard
                label="Preflight"
                value={preflightReady ? "Ready" : "Needs attention"}
                helper={syncPreflight?.recommended_next_action || "Run preflight after the backend endpoint is available."}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                label="Last traffic import"
                value={dateText(preflightRunTypes.listing_traffic_import?.last_success?.completed_at) || "No successful run"}
                helper={runCountSummary(preflightRunTypes.listing_traffic_import?.last_success)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                label="Latest failure"
                value={latestRoutineFailure ? latestRoutineFailure.label : "No failure recorded"}
                helper={latestRoutineFailure ? failureText(latestRoutineFailure.failure) : "Run history has no failed or partial run."}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={1.5}>
              {routineSteps.map((step, index) => (
                <Box
                  key={step.key}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "36px minmax(180px, 1.2fr) minmax(180px, 1.6fr) minmax(180px, 1.6fr) auto" },
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>{index + 1}</Typography>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{step.label}</Typography>
                      {runStatusChip(step, preflightReady)}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{step.helper}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Last success</Typography>
                    <Typography variant="body2">
                      {step.key === "preflight"
                        ? (preflightReady ? "Current preflight is ready" : "Preflight needs attention")
                        : (dateText(step.lastSuccess?.completed_at) || "No successful run recorded")}
                    </Typography>
                    {step.key !== "preflight" && (
                      <Typography variant="caption" color="text.secondary">{runCountSummary(step.lastSuccess)}</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Failure detail</Typography>
                    <Typography variant="body2" color={step.lastFailure ? "warning.main" : "text.secondary"}>
                      {step.lastFailure ? failureText(step.lastFailure) : "No failed run recorded"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                    {step.key === "preflight" && (
                      <Button size="small" variant="outlined" onClick={loadData} disabled={loading || actionLoading}>
                        Refresh
                      </Button>
                    )}
                    {step.actionPath && (
                      <Button
                        size="small"
                        variant={index <= 3 ? "contained" : "outlined"}
                        onClick={() => runAction(step.actionPath, step.actionBody, step.successPrefix)}
                        disabled={loading || actionLoading || schemaIssues.length > 0}
                      >
                        Run
                      </Button>
                    )}
                    {step.key === "action_queue_review" && (
                      <Button size="small" variant="contained" onClick={() => jumpToActionQueue()}>
                        Open
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Additional manual controls
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => runAction("/snapshots/local-baseline", { limit: 1000 }, "Local baseline snapshots created")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Create Local Baseline Snapshot
            </Button>
            <Button
              variant="contained"
              onClick={() => runAction("/listings/sync-from-ebay", { limit: 5, filter_mode: "stale_listed" }, "Synced 5 stale listings from eBay")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Sync 5 Stale Listings
            </Button>
            <Button
              variant="contained"
              onClick={() => runAction("/listings/sync-from-ebay", { limit: 25, filter_mode: "stale_listed" }, "Synced 25 stale listings from eBay")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Sync 25 Stale Listings
            </Button>
            <Button
              variant="outlined"
              onClick={() => runAction("/listings/sync-from-ebay", { limit: 25, filter_mode: "failed" }, "Retried 25 failed listing syncs")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Retry 25 Failed Syncs
            </Button>
            <Button
              variant="outlined"
              onClick={() => runAction("/listings/sync-from-ebay", { limit: 50, filter_mode: "listed" }, "Synced 50 listed listings from eBay")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Sync 50 Listed Listings
            </Button>
            <Button
              variant="contained"
              onClick={() => runAction("/imports/listing-traffic", { days: 30, limit: 1000 }, "Imported listing traffic")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Import Listing Traffic
            </Button>
            <Button
              variant="contained"
              onClick={() => runAction("/imports/promoted-listings", { days: 30 }, "Imported Promoted Listings performance")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Import Promoted Listing Performance
            </Button>
            <Button
              variant="contained"
              onClick={() => runAction("/imports/priority-cpc", { days: 30 }, "Imported Priority CPC performance")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Import Priority CPC Performance
            </Button>
            <Button
              variant="outlined"
              onClick={() => runAction("/imports/completed-ad-reports", { limit: 10 }, "Imported completed ad report files")}
              disabled={loading || actionLoading || schemaIssues.length > 0}
            >
              Import Completed Ad Reports
            </Button>
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
These sync/import buttons are eBay to app only. They pull current eBay offer/listing details, Analytics traffic data, Marketing campaign/report metadata, and completed Marketing TSV_GZIP report rows into local tables. They do not push price, quantity, title, status, bid, budget, or campaign changes to eBay.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Manual DB install status
            </Typography>
            <Stack spacing={1}>
              {Object.entries(schemaTables).map(([name, status]) => (
                <Alert key={name} severity={status?.ready ? "success" : "warning"}>
                  <strong>{name}</strong>: {status?.ready ? "ready" : status?.exists ? `missing columns: ${(status?.missing_columns || []).join(", ")}` : "missing table"}
                </Alert>
              ))}
            </Stack>
          </Box>
          </Paper>
        </Box>
      )}
      {tab === 8 && (
        <DailyDecisionAgentPanel
          onOpenActionQueue={() => jumpToActionQueue()}
          onOpenImports={() => setTab(7)}
        />
      )}
      {tab === 9 && (
        <AdStopLossPanel />
      )}
      {tab === 10 && (
        <BusinessFeedbackPanel onOpenActionQueue={() => jumpToActionQueue()} />
      )}
    </Box>
  );
}



